import React, { useEffect, useRef } from "react"
import { Provider } from "react-redux"
import { createRoot } from "react-dom/client"
import InternalLink from "./internallink/InternalLink"
import { store } from "../../../../store"
import {
    buildHtmlWithPlaceholders,
    buildTextWithPlaceholders,
} from "./utils/link-parser"
export interface Props {
    content?: string
    parseHTML?: boolean
}

export default function HTMLAndInternalLinkParser(props: Props): JSX.Element {
    const { content = "", parseHTML = true } = props
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const container = containerRef.current
        if (!container) {
            return
        }

        let cancelled = false

        const run = async (): Promise<void> => {
            const html = parseHTML
                ? await buildHtmlWithPlaceholders(content)
                : await buildTextWithPlaceholders(content)

            if (cancelled) {
                return
            }

            // Inject HTML directly via ref (bypasses React's render cycle)
            container.innerHTML = html

            /* ------------------------------------------------------
             * Execute hoisted scripts
             *
             * innerHTML does not run <script>. We recreate each one
             * as a fresh element so the browser executes it.
             * ------------------------------------------------------ */
            const scriptContainer =
                container.querySelector<HTMLDivElement>("#script")
            if (scriptContainer) {
                const oldScripts = Array.from(
                    scriptContainer.querySelectorAll("script"),
                )
                oldScripts.forEach((oldScript) => {
                    const newScript = document.createElement("script")

                    for (const attr of Array.from(oldScript.attributes)) {
                        newScript.setAttribute(attr.name, attr.value)
                    }
                    // Copy inline content if present
                    newScript.textContent = oldScript.textContent
                    // Replacing forces the browser to execute it
                    oldScript.replaceWith(newScript)
                })
            }

            // Mount React components into the placeholder spans
            const mounts =
                container.querySelectorAll<HTMLElement>("[data-ilink-mount]")

            mounts.forEach((element) => {
                const url = element.getAttribute("data-ilink-url") || ""
                const root = createRoot(element)
                root.render(
                    <Provider store={store}>
                        <InternalLink url={url} />
                    </Provider>,
                )
            })
        }

        run()

        return () => {
            cancelled = true
        }
    }, [content, parseHTML])

    return <div ref={containerRef} />
}
